import React, { FC, useCallback, useMemo, useRef, useState } from "react";
import styles from "./LidarBottomMesh.module.less";
import { Canvas, ThreeEvent } from "@react-three/fiber";
import {
  OrbitControls,
  PerspectiveCamera,
  Plane,
  useBVH,
} from "@react-three/drei";

import {
  DoubleSide,
  DynamicDrawUsage,
  LinearToneMapping,
  Mesh,
  SRGBColorSpace,
} from "three";
import { getUnit } from "~/helpers/userPreference";
import { UNITS } from "~/constants/unit";
import {
  calculateDelaunayDataForBottom,
  getMeshTooltip,
} from "../../../../helpers/meshCalculator";
import { LidarDataVisualizationZone } from "../../../../../../types/getLiDARDataVisualizationSummariesForLastSpecificHeats";
import { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { Button } from "antd";
interface LidarBottomMeshProps {
  visualizationZoneData: LidarDataVisualizationZone[];
  isExpand: boolean;
}

const LidarBottomMesh: React.FC<LidarBottomMeshProps> = ({
  visualizationZoneData,
  isExpand,
}) => {
  const tooltipRef = useRef<HTMLSpanElement | null>(null);

  //Fatih, INFO:
  //Need just rerender <mesh> comp
  //If rerender whole component it has blink effect during slider plays

  //INFO: data is a huge array and it is not necessary to check it for changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const meshKey = useMemo(() => Math.random(), [visualizationZoneData.length]);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const controlsRef = useRef<OrbitControlsImpl | null>(null);

  const [isZoomed, setIsZoomed] = useState<boolean>(false);

  const clearTooltip = useCallback(() => {
    if (tooltipRef.current && canvasRef.current) {
      tooltipRef.current.style.display = "none";
      canvasRef.current.style.cursor = "default";
    }
  }, []);

  const handleResetCamera = useCallback(() => {
    if (controlsRef.current) {
      controlsRef.current.reset();
      controlsRef.current.update();
      setIsZoomed(false);
    }
  }, []);

  const handleRightClick = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if ((event.ctrlKey && event.button === 0) || event.button === 2) {
        //Right click and ctrl + leftclick
        event.preventDefault();
        setIsZoomed(true);
      }
    },
    [],
  );

  return (
    <div className={styles.lidarBottomMesh}>
      {isZoomed && isExpand && (
        <Button
          className={styles.resetCameraButton}
          size={"large"}
          onClick={handleResetCamera}
        >
          Reset Camera
        </Button>
      )}
      <span className={styles.tooltip} ref={tooltipRef} />
      <Canvas
        gl={{
          outputColorSpace: SRGBColorSpace,
          toneMapping: LinearToneMapping,
        }}
        ref={canvasRef}
        onPointerDown={handleRightClick}
        onMouseOut={clearTooltip}
        onPointerOut={clearTooltip}
        onWheel={() => {
          setIsZoomed(true);
        }}
      >
        <PerspectiveCamera
          makeDefault
          position={[0, 0, 20]}
          rotation={[0, 0, 0]}
        />
        <spotLight position={[0, 0, 30]} color={"#ffffff"} intensity={2000} />
        <Plane args={[1, 1]} scale={isExpand ? [1.4, 1.4, 1] : [1.1, 1.1, 1]}>
          <BottomMesh
            key={meshKey}
            visualizationZoneData={visualizationZoneData}
            tooltipRef={tooltipRef}
            canvasRef={canvasRef}
            isExpanded={isExpand}
            clearTooltip={clearTooltip}
          />
        </Plane>

        <OrbitControls
          ref={controlsRef}
          enableRotate={false}
          maxDistance={20}
          enableZoom={isExpand}
        />
      </Canvas>
    </div>
  );
};

export default LidarBottomMesh;

interface BottomMeshProps {
  visualizationZoneData: LidarDataVisualizationZone[];
  tooltipRef: React.MutableRefObject<HTMLSpanElement | null>;
  canvasRef: React.MutableRefObject<HTMLCanvasElement | null>;
  isExpanded: boolean;
  clearTooltip: () => void;
}

const BottomMesh: FC<BottomMeshProps> = ({
  visualizationZoneData,
  tooltipRef,
  canvasRef,
  isExpanded,
  clearTooltip,
}) => {
  const meshRef = useRef<Mesh>();

  useBVH(meshRef);

  const { points, meshIndex, colors } = useMemo(() => {
    return calculateDelaunayDataForBottom(visualizationZoneData);
    //INFO: data is a huge array and it is not necessary to check it for changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visualizationZoneData.length]);

  const hoverHandler = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (event.intersections.length === 0) {
        return;
      }

      const { course, quadrant, thickness } = getMeshTooltip(
        event.intersections[0],
        false,
      );

      requestAnimationFrame(() => {
        if (tooltipRef.current && canvasRef.current) {
          canvasRef.current.style.cursor = "crosshair";
          tooltipRef.current.style.display = "unset";
          tooltipRef.current.style.left = `${
            event.offsetX + (isExpanded ? 120 : 0)
          }px`;
          tooltipRef.current.style.top = `${
            event.offsetY + (isExpanded ? -20 : -50)
          }px`;
          tooltipRef.current.innerHTML = `<div className="${
            styles.tooltip
          }"><div>${thickness}${getUnit(
            UNITS.MILLIMETER,
          )}</div><div style="font-size: 14px">Course: ${course}</div> <div style="font-size: 14px">Clock: ${quadrant}</div></div>`;
        }
      });
    },
    [canvasRef, isExpanded, tooltipRef],
  );

  return (
    <mesh
      rotation={[0, 0, 0]}
      position={[0, 0.7, 0]}
      onPointerMove={hoverHandler}
      onPointerLeave={clearTooltip}
    >
      <bufferGeometry attach="geometry">
        <bufferAttribute
          array={meshIndex}
          attach="index"
          count={meshIndex ? meshIndex.length : 0}
          itemSize={1}
          usage={DynamicDrawUsage}
        />
        <bufferAttribute
          attach="attributes-position"
          array={points}
          count={points ? points.length / 3 : 0}
          itemSize={3}
          usage={DynamicDrawUsage}
        />
        <bufferAttribute
          attach="attributes-color"
          array={colors}
          count={colors ? colors.length / 3 : 0}
          itemSize={3}
          usage={DynamicDrawUsage}
        />
      </bufferGeometry>
      <meshLambertMaterial
        vertexColors
        transparent={false}
        side={DoubleSide}
        flatShading
      />
    </mesh>
  );
};
